import { useMemo, useState } from 'react';
import {
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import ResourceGallery from './ResourceGallery';
import ResourceThumbnail from './ResourceThumbnail';
import type { GalleryItem } from './ResourceGallery';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 111px)',
    gap: tokens.spacingHorizontalS,
  },
  clickable: {
    cursor: 'pointer',
    padding: 0,
    border: 'none',
    backgroundColor: 'transparent',
    textAlign: 'start' as const,
  },
});

interface AdditionalImagesTabProps {
  imageIds: string[];
  domainId: number;
}

export default function AdditionalImagesTab({ imageIds, domainId }: AdditionalImagesTabProps) {
  const styles = useStyles();
  const { t } = useTranslation();
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const galleryItems: GalleryItem[] = useMemo(
    () => imageIds.map(id => ({ fileId: id, type: 'image' as const })),
    [imageIds],
  );

  return (
    <div className={styles.container}>
      <Text weight="semibold">{t('additionalImages')}</Text>
      <div className={styles.grid}>
        {imageIds.map((id, index) => (
          <button key={id} className={styles.clickable} onClick={() => setSelectedIndex(index)} aria-label={t('images')}>
            <ResourceThumbnail domainId={domainId} fileId={id} />
          </button>
        ))}
      </div>
      {selectedIndex !== null && (
        <ResourceGallery
          items={galleryItems}
          startIndex={selectedIndex}
          domainId={domainId}
          onClose={() => setSelectedIndex(null)}
        />
      )}
    </div>
  );
}
